import { useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";

import { useIoCContext } from "@context/IoCContext/IoCContext";
import { Types } from "@ioc/types";

import { IUpdateUserService } from "@modules/user/models/IUpdateUserService";
import { IUpdateUserDTO } from "@modules/user/dtos/IUpdateUserDTO";
import { IUser } from "@utils/interfaces";
import ROUTES from "@config/routes";

import FormEditUserValidationSchema from "./validation";
import { IFormEditUser } from "./interface";
import { updateUser } from "./updateUser";

export function useSubmitEditUser() {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const { id } = useParams<{ id: string }>();

  const { state: stateLocation } = useLocation<{ user: IUser }>();

  const [loading, setLoading] = useState(false);

  const iocContext = useIoCContext();

  const service = iocContext.serviceContainer.get<IUpdateUserService>(
    Types.Users.IUpdateUSerService
  );

  const userFromState = stateLocation?.user;
  const user = userFromState && userFromState.id === id ? userFromState : null;

  const initialValues: IFormEditUser | null = user
    ? {
        firstName: user.firstName,
        lastName: user.lastName,
        companyName: "",
        CNPJ: "",
        email: user.email,
        username: user.username,
        attributes: user.attributes,
        profile: user.kind,
      }
    : null;

  const handleSubmit = async (values: IFormEditUser) => {
    if (!user) return;

    setLoading(true);
    delete values.companyName;
    delete values.CNPJ;

    const casted = FormEditUserValidationSchema.cast(values) as
      | IFormEditUser
      | undefined;
    if (!casted) {
      setLoading(false);
      return;
    }

    const updateData: IUpdateUserDTO = {
      ...casted,
      userID: user.id,
      attributes: {
        INTERNAL_NAME: casted.attributes?.INTERNAL_NAME,
        BPID: casted.attributes?.BPID,
        CNPJ: casted.attributes?.CNPJ ?? [],
      },
    };

    const { data, error } = await updateUser(service, updateData);

    if (error) {
      enqueueSnackbar(error.message, { variant: error.variant });
    } else if (data) {
      enqueueSnackbar("Usuário alterado com sucesso", { variant: "success" });
      history.push(ROUTES.ADMIN_ROUTES.LIST_USERS);
    }

    setLoading(false);
  };

  return { loading, initialValues, handleSubmit, hasUser: !!user };
}
